import React, { useRef, useState } from "react";
import { letterInkForHex } from "../lib/harmonicPalette";
import {
  moveFocusByArrowKey,
  nextWrappedLinearIndex,
} from "../lib/arrowFocus";

/**
 * Run-length recipe for the stringing sequence, under the bead grid. Read
 * down each column, then across. A detected repeat states its multiplier
 * before the runs it applies to, so you know how many times to work them.
 */
const BeadOrder = ({
  groups,
  getLetterForHex,
  activeRunKey = null,
  onRunActivate = null,
}) => {
  const rootRef = useRef(null);
  const [focusedRunKey, setFocusedRunKey] = useState(null);

  if (!groups || groups.length === 0) {
    return null;
  }

  const runKeys = [];
  groups.forEach((group, groupIndex) => {
    if (group.type === "repeat") {
      (group.pattern || []).forEach((_, idx) => {
        runKeys.push(`g${groupIndex}-s${idx}`);
      });
    } else if (group.type !== "divide") {
      runKeys.push(`g${groupIndex}`);
    }
  });
  const tabStopKey =
    focusedRunKey && runKeys.includes(focusedRunKey)
      ? focusedRunKey
      : runKeys[0];

  const activate = (runKey) => {
    setFocusedRunKey(runKey);
    if (typeof onRunActivate === "function") onRunActivate(runKey);
  };

  const handleRunKeyDown = (event) => {
    const root = event.currentTarget.closest(".bead-order");
    if (!root) return;
    const items = Array.from(root.querySelectorAll(".bead-order-item"));
    if (event.key === "Tab") {
      const from = items.indexOf(event.currentTarget);
      const next = nextWrappedLinearIndex(from, items.length, event.shiftKey);
      if (next < 0) return;
      event.preventDefault();
      items[next]?.focus();
      return;
    }
    moveFocusByArrowKey(event, { items });
  };

  const runLine = ({ color, count }, key, runKey) => {
    const letter = getLetterForHex(color);
    const label = letter
      ? `${letter} × ${count}`
      : `${color} × ${count}`;
    const isLinked = activeRunKey != null && activeRunKey === runKey;
    const isTabStop = tabStopKey === runKey;

    return (
      <li
        key={key}
        className={`bead-order-item${isLinked ? " is-linked" : ""}`}
        tabIndex={isTabStop ? 0 : -1}
        aria-label={label}
        data-run-key={runKey}
        onClick={(event) => {
          event.currentTarget.focus();
        }}
        onKeyDown={handleRunKeyDown}
        onFocus={(event) => {
          activate(runKey);
          event.currentTarget.scrollIntoView?.({
            block: "nearest",
            inline: "nearest",
          });
        }}
      >
        <span
          className="swatch"
          style={{ backgroundColor: color, color: letterInkForHex(color) }}
        >
          {letter}
        </span>
        <span className="bead-order-item__count">× {count}</span>
      </li>
    );
  };

  return (
    <ol className="bead-order" ref={rootRef}>
      {groups.map((group, groupIndex) => {
        if (group.type === "repeat") {
          return (
            <li
              key={`repeat-${groupIndex}`}
              className="bead-order-repeat-group"
            >
              <p className="bead-order-repeat-group__title">
                Repeat {group.repeatCount}×
              </p>
              <ol className="bead-order-repeat-group__items">
                {group.pattern.map((run, idx) =>
                  runLine(
                    run,
                    `${run.color}-${run.originalIndex}-${idx}`,
                    `g${groupIndex}-s${idx}`
                  )
                )}
              </ol>
            </li>
          );
        }

        if (group.type === "divide") {
          return (
            <li key={`divide-${groupIndex}`} className="bead-order__divide">
              Dissolve
            </li>
          );
        }

        return runLine(
          group.item,
          `${group.item.color}-${group.item.originalIndex}`,
          `g${groupIndex}`
        );
      })}
    </ol>
  );
};

export default BeadOrder;
